import { Component, OnInit, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ProductService } from '../../services/product.service';
import { SettingsService } from '../../services/settings.service';
import { WishlistService } from '../../services/wishlist.service';
import { AuthService } from '../../services/auth.service';
import { CartService } from '../../services/cart.service';
import { CategoryIconService } from '../../services/category-icon.service';
import { PlatformService } from '../../services/platform.service';
import { ArkifiHubPlatformService } from '../../services/arkifihub-platform.service';
import { ShoppingCardService, ArkifiHubShoppingCardResponseDto } from '../../services/shopping-card.service';
import { ProductDto, ProductCategoryDto, BrandDto } from '../../models/product.dto';
import { InputNumberModule } from 'primeng/inputnumber';

@Component({
  selector: 'app-store',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    ProgressSpinnerModule,
    InputTextModule,
    InputNumberModule,
    DialogModule,
    ToastModule
  ],
  providers: [MessageService],
  templateUrl: './store.component.html',
  styleUrl: './store.component.css'
})
export class StoreComponent implements OnInit {
  platformId = signal<string>('');
  platformName = signal<string>('');
  platformLogo = signal<string | null>(null);
  platformProfile = signal<{
    businessName: string | null;
    businessEmail: string | null;
    businessPhoneNumber: string | null;
    businessAddress: string | null;
  } | null>(null);
  isBillGatewayConfigured = signal<boolean>(false);
  supportsSavedCards = signal<boolean>(false);
  products = signal<ProductDto[]>([]);
  categories = signal<ProductCategoryDto[]>([]);
  brands = signal<BrandDto[]>([]);
  loading = signal(true);
  error = signal<string | null>(null);
  
  searchQuery = signal('');
  selectedCategoryId = signal<number | null>(null);
  selectedBrandId = signal<string | null>(null);
  
  // Filter modal state
  showFilterModal = signal(false);
  selectedCategories = signal<Set<number>>(new Set());
  selectedBrands = signal<Set<string>>(new Set());
  filtering = signal(false);
  
  // Wishlist tracking
  wishlistProductIds = signal<Set<string>>(new Set());
  
  // Pagination
  pageNumber = signal(1);
  pageSize = 20;
  
  // Shopping card state
  shoppingCard = signal<ArkifiHubShoppingCardResponseDto | null>(null);
  shoppingCardLoading = signal(false);
  shoppingCardExists = signal(false);
  
  // Modal states
  showGenerateCardModal = signal(false);
  showFundCardModal = signal(false);
  showStoreProfileModal = signal(false);
  fundingAmount = signal<number>(0);
  fundingLoading = signal(false);
  
  filteredProducts = computed(() => {
    let filtered = this.products();
    const query = this.searchQuery().toLowerCase().trim();
    
    if (query) {
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(query) ||
        (p.description && p.description.toLowerCase().includes(query))
      );
    }
    
    return filtered;
  });

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private productService: ProductService,
    private settingsService: SettingsService,
    private wishlistService: WishlistService,
    public authService: AuthService,
    private cartService: CartService,
    private messageService: MessageService,
    private categoryIconService: CategoryIconService,
    private platformService: PlatformService,
    private arkifiHubPlatformService: ArkifiHubPlatformService,
    private shoppingCardService: ShoppingCardService
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      const id = params['id'];
      let name = params['name'] || 'Store';
      
      if (id && id.trim() !== '') {
        try {
          // Decode name if it exists
          if (name && name !== 'Store') {
            name = decodeURIComponent(name);
          }
          
          this.platformId.set(id);
          this.platformName.set(name);
          this.loadPlatformLogo(id);
          this.loadPlatformProfile(id);
          this.loadSupportsSavedCards(id);
          this.loadStoreData();
          
          // Initialize cart if user is authenticated
          if (this.authService.isAuthenticated()) {
            this.cartService.initializeCart(id);
            // Check shopping card status
            this.checkShoppingCard();
          }
        } catch (error) {
          console.error('Error initializing store component:', error);
          this.error.set('Failed to load store. Invalid URL parameters.');
          this.loading.set(false);
        }
      } else {
        this.error.set('Platform ID is required');
        this.loading.set(false);
      }
    });
  }

  loadPlatformLogo(platformId: string): void {
    // Load all platforms and find the one matching our platformId
    this.platformService.getPlatforms().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const platform = response.data.find(p => p.id === platformId);
          if (platform) {
            this.platformLogo.set(platform.platformLogo);
          }
        }
      },
      error: (err) => {
        console.error('Error loading platform logo:', err);
        // Don't set error, just continue without logo
      }
    });
  }

  loadPlatformProfile(platformId: string): void {
    this.platformService.getPlatformProfile(platformId).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const profile = response.data;
          // Update platform name and logo from profile
          this.platformName.set(profile.platformName);
          this.platformLogo.set(profile.platformLogo);
          
          // Store profile information
          this.platformProfile.set({
            businessName: profile.businessName,
            businessEmail: profile.businessEmail,
            businessPhoneNumber: profile.businessPhoneNumber,
            businessAddress: profile.businessAddress
          });
          
          // Store bills payment configuration status
          this.isBillGatewayConfigured.set(profile.isBillGatewayConfigured || false);
        }
      },
      error: (err) => {
        console.error('Error loading platform profile:', err);
        // Don't set error, just continue without profile info
        // Default to false for bills payment configuration
        this.isBillGatewayConfigured.set(false);
      }
    });
  }

  loadSupportsSavedCards(platformId: string): void {
    const environment = this.settingsService.getEnvironment();
    this.arkifiHubPlatformService.getSupportsSavedCards(platformId, environment).subscribe({
      next: (res) => {
        this.supportsSavedCards.set(res.data?.supportsSavedCards ?? false);
      },
      error: () => {
        this.supportsSavedCards.set(false);
      }
    });
  }

  loadStoreData(): void {
    const platformId = this.platformId();
    
    // Validate platform ID before proceeding
    if (!platformId || platformId.trim() === '') {
      this.error.set('Invalid platform ID');
      this.loading.set(false);
      return;
    }
    
    this.loading.set(true);
    this.error.set(null);
    
    const environment = this.settingsService.getEnvironment();
    
    // Load products
    this.productService.getProducts(platformId, environment, {
      categoryId: this.selectedCategoryId() || undefined,
      brandId: this.selectedBrandId() || undefined,
      pageNumber: this.pageNumber(),
      pageSize: this.pageSize
    }).subscribe({
      next: (response) => {
        if (response.success) {
          this.products.set(response.data);
          // Load wishlist status for authenticated users
          if (this.authService.isAuthenticated()) {
            this.loadWishlistStatus();
          }
        } else {
          this.error.set(response.message || 'Failed to load products');
        }
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set('Failed to load products. Please try again later.');
        this.loading.set(false);
        console.error('Error loading products:', err);
      }
    });
    
    // Load categories
    this.productService.getCategories(platformId, environment).subscribe({
      next: (response) => {
        if (response.success) {
          this.categories.set(response.data);
        }
      },
      error: (err) => {
        console.error('Error loading categories:', err);
      }
    });
    
    // Load brands
    this.productService.getBrands(platformId, environment).subscribe({
      next: (response) => {
        if (response.success) {
          this.brands.set(response.data);
        }
      },
      error: (err) => {
        console.error('Error loading brands:', err);
      }
    });
  }

  onCategoryChange(categoryId: number | null): void {
    this.selectedCategoryId.set(categoryId);
    this.pageNumber.set(1);
    this.loadStoreData();
  }

  onBrandChange(brandId: string | null): void {
    this.selectedBrandId.set(brandId);
    this.pageNumber.set(1);
    this.loadStoreData();
  }

  onSearch(): void {
    // Search is handled via computed signal, no need to reload
  }

  openFilterModal(): void {
    // Initialize selections from current filters
    const selectedCats = new Set<number>();
    const selectedBrandsSet = new Set<string>();
    
    if (this.selectedCategoryId() !== null) {
      selectedCats.add(this.selectedCategoryId()!);
    }
    if (this.selectedBrandId() !== null) {
      selectedBrandsSet.add(this.selectedBrandId()!);
    }
    
    this.selectedCategories.set(selectedCats);
    this.selectedBrands.set(selectedBrandsSet);
    this.showFilterModal.set(true);
  }

  closeFilterModal(): void {
    this.showFilterModal.set(false);
  }

  toggleCategory(categoryId: number): void {
    const current = this.selectedCategories();
    if (current.has(categoryId)) {
      current.delete(categoryId);
    } else {
      current.add(categoryId);
    }
    this.selectedCategories.set(new Set(current));
  }

  toggleBrand(brandId: string): void {
    const current = this.selectedBrands();
    if (current.has(brandId)) {
      current.delete(brandId);
    } else {
      current.add(brandId);
    }
    this.selectedBrands.set(new Set(current));
  }

  isCategorySelected(categoryId: number): boolean {
    return this.selectedCategories().has(categoryId);
  }

  isBrandSelected(brandId: string): boolean {
    return this.selectedBrands().has(brandId);
  }

  applyFilters(): void {
    this.closeFilterModal();
    this.filtering.set(true);
    
    // Get first selected category and brand (or null if none selected)
    const selectedCats = Array.from(this.selectedCategories());
    const selectedBrandsArray = Array.from(this.selectedBrands());
    
    this.selectedCategoryId.set(selectedCats.length > 0 ? selectedCats[0] : null);
    this.selectedBrandId.set(selectedBrandsArray.length > 0 ? selectedBrandsArray[0] : null);
    
    this.pageNumber.set(1);
    
    // Reload products with filters
    const platformId = this.platformId();
    const environment = this.settingsService.getEnvironment();
    
    this.productService.getProducts(platformId, environment, {
      categoryId: this.selectedCategoryId() || undefined,
      brandId: this.selectedBrandId() || undefined,
      pageNumber: this.pageNumber(),
      pageSize: this.pageSize
    }).subscribe({
      next: (response) => {
        if (response.success) {
          this.products.set(response.data);
          if (this.authService.isAuthenticated()) {
            this.loadWishlistStatus();
          }
        }
        this.filtering.set(false);
      },
      error: (err) => {
        this.error.set('Failed to filter products. Please try again later.');
        this.filtering.set(false);
        console.error('Error filtering products:', err);
      }
    });
  }

  onProductClick(product: ProductDto): void {
    // Navigate to product detail page
    const encodedName = encodeURIComponent(this.platformName());
    this.router.navigate(['/store', this.platformId(), encodedName, 'product', product.id]);
  }

  getProductImage(product: ProductDto): string {
    if (product.images) {
      try {
        const images = typeof product.images === 'string' 
          ? JSON.parse(product.images) 
          : product.images;
        if (Array.isArray(images) && images.length > 0) {
          return images[0];
        }
      } catch (e) {
        // If parsing fails, try using as string directly
        if (typeof product.images === 'string' && product.images.trim()) {
          return product.images;
        }
      }
    }
    return '/assets/placeholder-product.png';
  }

  formatPrice(price: number): string {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(price);
  }

  getCategoryIcon(category: ProductCategoryDto): string {
    return this.categoryIconService.getCategoryIcon(category.name);
  }

  getBrandLogo(brand: BrandDto | null): string | null {
    if (brand?.logoUrl && brand.logoUrl.trim()) {
      return brand.logoUrl;
    }
    return null;
  }

  goBack(): void {
    this.router.navigate(['/marketplace']);
  }

  loadWishlistStatus(): void {
    const platformId = this.platformId();
    if (!platformId) return;

    // Check wishlist status for all products
    this.products().forEach(product => {
      this.wishlistService.isProductInWishList(product.id, platformId).subscribe({
        next: (response) => {
          if (response.success && response.data) {
            const currentIds = this.wishlistProductIds();
            currentIds.add(product.id);
            this.wishlistProductIds.set(new Set(currentIds));
          }
        },
        error: (err) => {
          console.error('Error checking wishlist status:', err);
        }
      });
    });
  }

  isInWishlist(productId: string): boolean {
    return this.wishlistProductIds().has(productId);
  }

  toggleWishlist(product: ProductDto, event: Event): void {
    event.stopPropagation(); // Prevent product click

    if (!this.authService.isAuthenticated()) {
      this.router.navigate(['/signin'], { queryParams: { returnUrl: this.router.url } });
      return;
    }

    const platformId = this.platformId();
    if (!platformId) return;

    if (this.isInWishlist(product.id)) {
      // Remove from wishlist - we need the wishlist item ID
      // For now, we'll just show a message that they need to go to wishlist page
      this.messageService.add({
        severity: 'info',
        summary: 'Info',
        detail: 'Please go to your wishlist to remove items'
      });
    } else {
      // Add to wishlist
      this.wishlistService.addToWishList({
        productId: product.id
      }).subscribe({
        next: (response) => {
          if (response.success) {
            const currentIds = this.wishlistProductIds();
            currentIds.add(product.id);
            this.wishlistProductIds.set(new Set(currentIds));
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Product added to wishlist'
            });
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: response.message || 'Failed to add to wishlist'
            });
          }
        },
        error: (err) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to add product to wishlist'
          });
          console.error('Error adding to wishlist:', err);
        }
      });
    }
  }

  goToWishlist(): void {
    if (!this.authService.isAuthenticated()) {
      this.router.navigate(['/signin'], { queryParams: { returnUrl: '/wishlist' } });
      return;
    }
    const platformId = this.platformId();
    const platformName = this.platformName();
    if (platformId) {
      // Pass platform name in query params so wishlist can navigate back
      this.router.navigate(['/wishlist', platformId], { 
        queryParams: { name: encodeURIComponent(platformName) }
      });
    }
  }

  addToCart(product: ProductDto, event: Event): void {
    event.stopPropagation(); // Prevent product click

    if (!this.authService.isAuthenticated()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Authentication Required',
        detail: 'Please sign in to add items to your cart',
        life: 3000
      });
      setTimeout(() => {
        this.router.navigate(['/signin'], { queryParams: { returnUrl: this.router.url } });
      }, 1500);
      return;
    }

    // Verify token exists
    const token = this.authService.getToken();
    if (!token) {
      this.messageService.add({
        severity: 'error',
        summary: 'Session Expired',
        detail: 'Your session has expired. Please sign in again.',
        life: 4000
      });
      this.authService.logout();
      setTimeout(() => {
        this.router.navigate(['/signin'], { queryParams: { returnUrl: this.router.url } });
      }, 1500);
      return;
    }

    if (product.stockQuantity <= 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Out of Stock',
        detail: 'This product is currently out of stock',
        life: 3000
      });
      return;
    }

    const platformId = this.platformId();
    if (!platformId) return;

    this.cartService.addToCart({
      productId: product.id,
      quantity: 1
    }, platformId).subscribe({
      next: (response) => {
        if (response.success) {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Product added to cart',
            life: 3000
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: response.message || 'Failed to add to cart',
            life: 4000
          });
        }
      },
      error: (err) => {
        let errorMessage = 'Failed to add product to cart';
        
        if (err.status === 401 || err.error?.message?.includes('token') || err.error?.message?.includes('authentication')) {
          errorMessage = 'Your session has expired. Please sign in again.';
          this.authService.logout();
          setTimeout(() => {
            this.router.navigate(['/signin'], { queryParams: { returnUrl: this.router.url } });
          }, 2000);
        } else if (err.error?.message) {
          errorMessage = err.error.message;
        } else if (err.message) {
          errorMessage = err.message;
        }
        
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: errorMessage,
          life: 4000
        });
        console.error('Error adding to cart:', err);
      }
    });
  }

  goToCheckout(): void {
    if (!this.authService.isAuthenticated()) {
      this.router.navigate(['/signin'], { queryParams: { returnUrl: '/checkout' } });
      return;
    }
    const platformId = this.platformId();
    if (platformId) {
      this.router.navigate(['/checkout', platformId]);
    }
  }

  getCustomerName(): string {
    const user = this.authService.getUser();
    if (user && user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`.toUpperCase();
    }
    return 'CARDHOLDER NAME';
  }

  checkShoppingCard(): void {
    const platformId = this.platformId();
    if (!platformId || !this.authService.isAuthenticated()) return;
    
    this.shoppingCardLoading.set(true);
    const environment = this.settingsService.getEnvironment();
    
    this.shoppingCardService.checkShoppingCardExists(platformId, environment).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const hasCard = response.data.hasCard;
          this.shoppingCardExists.set(hasCard);
          
          if (hasCard) {
            // Card exists - use data from check response if available, otherwise fetch full details
            if (response.data.cardNumber && response.data.balance !== undefined) {
              // Use data from check response directly
              const cardData = {
                cardNumber: response.data.cardNumber,
                balance: response.data.balance,
                platformId: platformId,
                businessId: '',
                environmentType: environment,
                dateCreated: '',
                dateUpdated: ''
              };
              this.shoppingCard.set(cardData as any);
              this.shoppingCardExists.set(true);
              this.shoppingCardLoading.set(false);
            } else {
              // Card exists but need full details
              this.loadShoppingCard();
            }
          } else {
            // Card doesn't exist
            this.shoppingCard.set(null);
            this.shoppingCardExists.set(false);
            this.shoppingCardLoading.set(false);
          }
        } else {
          this.shoppingCardExists.set(false);
          this.shoppingCard.set(null);
          this.shoppingCardLoading.set(false);
        }
      },
      error: (err) => {
        console.error('Error checking shopping card:', err);
        this.shoppingCardExists.set(false);
        this.shoppingCard.set(null);
        this.shoppingCardLoading.set(false);
      }
    });
  }

  loadShoppingCard(): void {
    const platformId = this.platformId();
    if (!platformId) {
      this.shoppingCardLoading.set(false);
      return;
    }
    
    const environment = this.settingsService.getEnvironment();
    
    this.shoppingCardService.getShoppingCard(platformId, environment).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.shoppingCard.set(response.data);
          this.shoppingCardExists.set(true);
        } else {
          this.shoppingCard.set(null);
          this.shoppingCardExists.set(false);
        }
        this.shoppingCardLoading.set(false);
      },
      error: (err) => {
        console.error('Error loading shopping card:', err);
        this.shoppingCard.set(null);
        this.shoppingCardExists.set(false);
        this.shoppingCardLoading.set(false);
      }
    });
  }

  openGenerateCardModal(): void {
    this.showGenerateCardModal.set(true);
  }

  closeGenerateCardModal(): void {
    this.showGenerateCardModal.set(false);
  }

  generateShoppingCard(): void {
    const platformId = this.platformId();
    if (!platformId) return;
    
    this.shoppingCardLoading.set(true);
    const environment = this.settingsService.getEnvironment();
    
    this.shoppingCardService.generateShoppingCard(platformId, environment).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.shoppingCard.set(response.data);
          this.shoppingCardExists.set(true);
          this.showGenerateCardModal.set(false);
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Shopping card generated successfully!',
            life: 3000
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: response.message || 'Failed to generate shopping card',
            life: 4000
          });
        }
        this.shoppingCardLoading.set(false);
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err.error?.message || 'Failed to generate shopping card',
          life: 4000
        });
        this.shoppingCardLoading.set(false);
        console.error('Error generating shopping card:', err);
      }
    });
  }

  onFundCardClick(): void {
    if (!this.authService.isAuthenticated()) {
      this.router.navigate(['/signin'], { queryParams: { returnUrl: this.router.url } });
      return;
    }
    
    if (!this.shoppingCardExists()) {
      // Show modal to generate card first
      this.showGenerateCardModal.set(true);
      return;
    }
    
    // Show fund card modal
    this.fundingAmount.set(0);
    this.showFundCardModal.set(true);
  }

  closeFundCardModal(): void {
    this.showFundCardModal.set(false);
    this.fundingAmount.set(0);
  }

  fundShoppingCard(): void {
    const amount = this.fundingAmount();
    const platformId = this.platformId();
    
    if (!amount || amount <= 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Invalid Amount',
        detail: 'Please enter a valid amount greater than 0',
        life: 3000
      });
      return;
    }
    
    if (!platformId) return;
    
    // Build redirect URL: baseurl/store/{platformId}/{encodedName}/payment-callback
    // Payment gateway will append trxref as query parameter
    const encodedName = encodeURIComponent(this.platformName());
    const baseUrl = window.location.origin;
    const redirectUrl = `${baseUrl}/store/${platformId}/${encodedName}/payment-callback`;
    
    this.fundingLoading.set(true);
    const environment = this.settingsService.getEnvironment();
    
    this.shoppingCardService.fundShoppingCard(platformId, environment, amount, redirectUrl).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          // Redirect to payment gateway
          const paymentUrl = response.data.authorizationUrl || response.data.checkoutUrl;
          if (paymentUrl) {
            window.location.href = paymentUrl;
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Payment URL not received',
              life: 4000
            });
            this.fundingLoading.set(false);
          }
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: response.message || 'Failed to initialize payment',
            life: 4000
          });
          this.fundingLoading.set(false);
        }
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err.error?.message || 'Failed to initialize payment',
          life: 4000
        });
        this.fundingLoading.set(false);
        console.error('Error funding shopping card:', err);
      }
    });
  }

  getShoppingCardNumber(): string {
    const card = this.shoppingCard();
    if (card && card.cardNumber) {
      // Format card number as XXXX XXXX XXXX (10 digits)
      const cleaned = card.cardNumber.replace(/\s/g, '');
      return cleaned.match(/.{1,4}/g)?.join(' ') || card.cardNumber;
    }
    return '0000 0000 00';
  }

  getCardBalance(): string {
    const card = this.shoppingCard();
    if (card && card.balance !== undefined) {
      return this.formatPrice(card.balance);
    }
    return '₦0.00';
  }

  // Navigate to bills payment page
  goToBillsPayment(): void {
    const platformId = this.platformId();
    const platformName = this.platformName();
    
    if (platformId && platformName) {
      const encodedName = encodeURIComponent(platformName);
      this.router.navigate(['/bills-payment', platformId, encodedName]);
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Error',
        detail: 'Unable to navigate to bills payment. Please try again.',
        life: 3000
      });
    }
  }

  // Navigate to profile page
  goToProfile(): void {
    this.router.navigate(['/profile']);
  }

  // Navigate to orders page
  goToOrders(): void {
    const platformId = this.platformId();
    const platformName = this.platformName();
    
    if (platformId && platformName) {
      const encodedName = encodeURIComponent(platformName);
      this.router.navigate(['/orders', platformId, encodedName]);
    } else {
      this.router.navigate(['/orders']);
    }
  }

  // Navigate to bills transactions page
  goToBillsTransactions(): void {
    const platformId = this.platformId();
    const platformName = this.platformName();
    
    if (platformId && platformName) {
      const encodedName = encodeURIComponent(platformName);
      this.router.navigate(['/bills-transactions', platformId, encodedName]);
    } else {
      this.router.navigate(['/bills-transactions']);
    }
  }

  // Navigate to cards page
  goToCards(): void {
    const platformId = this.platformId();
    const platformName = this.platformName();
    
    if (platformId && platformName) {
      // Store platform name in localStorage for back navigation
      localStorage.setItem('arkifihub_platformName', platformName);
      // Don't pass environment - it will be read from SettingsService
      this.router.navigate(['/cards'], { 
        queryParams: { 
          platformId: platformId
        } 
      });
    } else {
      this.router.navigate(['/cards']);
    }
  }

  // Show store profile modal
  openStoreProfileModal(): void {
    // Load profile if not already loaded
    const id = this.platformId();
    if (id && !this.platformProfile()) {
      this.loadPlatformProfile(id);
    }
    this.showStoreProfileModal.set(true);
  }

  closeStoreProfileModal(): void {
    this.showStoreProfileModal.set(false);
  }

  getSelectedCategoryName(): string {
    const selectedId = this.selectedCategoryId();
    if (!selectedId) {
      return 'All Products';
    }
    const category = this.categories().find(c => c.id === selectedId);
    return category?.name || 'Products';
  }
}
